class AudioService {
  constructor() {
    this.queue = [];
    this.isSpeaking = false;
    this.isUnlocked = false;
    this.synth = typeof window !== "undefined" ? window.speechSynthesis : null;
    if (typeof window !== "undefined") {
      const autoUnlock = () => {
        this.unlockAudio();
        window.removeEventListener("click", autoUnlock);
        window.removeEventListener("keydown", autoUnlock);
      };
      window.addEventListener("click", autoUnlock, { once: true });
      window.addEventListener("keydown", autoUnlock, { once: true });
    }
  }

  /**
   * Unlocks the browser's audio context by playing a silent audio snippet
   * and an empty speech utterance on user interaction.
   */
  unlockAudio() {
    if (this.isUnlocked) return;
    
    try {
      // 1. Unlock TTS
      if (this.synth) {
        const utterance = new SpeechSynthesisUtterance("");
        utterance.volume = 0;
        this.synth.speak(utterance);
      }

      // 2. Unlock HTML5 Audio
      const silentAudio = new Audio("data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA");
      silentAudio.play().catch(() => {});
      
      this.isUnlocked = true;
      console.log("[AudioService] Audio context successfully unlocked by user interaction.");
    } catch (e) {
      console.warn("[AudioService] Failed to unlock audio:", e);
    }
  }

  /**
   * Stops any ongoing speech or queued alerts immediately.
   */
  stop() {
    this.queue = [];
    this.isSpeaking = false;
    if (this.synth) {
      try {
        this.synth.cancel();
      } catch (e) {
        console.warn("[AudioService] Failed to cancel speech synthesis:", e);
      }
    }
    console.log("[AudioService] Audio playback stopped and queue cleared.");
  }

  /**
   * Queues an alert to be played.
   * @param {string} text - The text to be spoken via TTS.
   * @param {string} [soundUrl] - Optional URL for an audio file to play before speaking.
   */
  playAlert(text, soundUrl = null) {
    if (!text || typeof text !== "string") return;
    const cleanText = text.trim();
    if (!cleanText) return;

    // 1. Prevent duplicate alerts already in queue
    if (this.queue.some((item) => item.text === cleanText)) {
      return;
    }

    // 2. Prevent repeating the exact same alert within 15 seconds
    const now = Date.now();
    if (this.lastSpokenText === cleanText && now - (this.lastSpokenTime || 0) < 15000) {
      return;
    }

    // 3. Bound queue length so backlog never accumulates
    if (this.queue.length >= 2) {
      this.queue.shift();
    }

    console.log(`[AudioService] Queued Alert: "${cleanText}"`);
    this.queue.push({ text: cleanText, soundUrl });
    this.processQueue();
  }

  processQueue() {
    if (this.isSpeaking || this.queue.length === 0) {
      if (this.isSpeaking && this.queue.length > 0) {
        console.log(`[AudioService] Already speaking, waiting in queue...`);
      }
      return;
    }

    this.isSpeaking = true;
    const current = this.queue.shift();
    console.log(`[AudioService] Processing Alert: "${current.text}"`);

    let safetyTimer = null;
    const done = () => {
      if (safetyTimer) clearTimeout(safetyTimer);
      this.lastSpokenText = current?.text;
      this.lastSpokenTime = Date.now();
      this.isSpeaking = false;
      this.processQueue();
    };

    safetyTimer = setTimeout(() => {
      if (this.isSpeaking) {
        console.warn("[AudioService] Alert timed out, resetting queue state.");
        done();
      }
    }, 8000);

    const speakText = () => {
      if (!this.synth) {
        done();
        return;
      }

      // Ensure voices are loaded (sometimes takes a moment on first load)
      const utterance = new SpeechSynthesisUtterance(current.text);
      utterance.rate = 0.9; // Slightly slower for clarity
      
      utterance.onend = () => {
        done();
      };
      
      utterance.onerror = (e) => {
        console.error("TTS Error:", e);
        done();
      };

      this.synth.speak(utterance);
    };

    if (current.soundUrl) {
      const audio = new Audio(current.soundUrl);
      audio.onended = speakText;
      audio.onerror = speakText; // If sound fails, fallback to speech directly
      audio.play().catch((e) => {
        console.warn("Autoplay prevented for sound, skipping to TTS:", e);
        speakText(); // fallback if user hasn't interacted with document
      });
    } else {
      speakText();
    }
  }
}

export const audioAlertService = new AudioService();
